import { executionArtifactLineId } from '../families/execution-artifact.js';
import type { StackFramesSpec } from '../model.js';
import type { ComparisonSpec } from '../primitives/comparison.js';
import { artifactTerminalHeight, argumentHeight, fittedHeight } from './heights.js';
import type { GoLesson } from './types.js';

const TRUTH_FILE = 'go-context-propagation.json';
const GO = 'go1.24.4 windows/amd64';

/**
 * Cancellation has to travel.
 *
 * Two captures of one four-layer chain — handler → service → repository → db —
 * differing by a single argument. The severed run is declared cite:"run": it is
 * not reproducible, so the lesson quotes that one run verbatim and says so.
 */
const mechanismFigure = (width: number, height: number): StackFramesSpec => ({
        id: 'go-context-propagation.mechanism',
        family: 'stack-frames',
        view: 'isometric',
        viewport: { width, height },
        growth: 'down',
        frames: [
          {
            id: 'handler',
            functionName: 'main.handler',
            status: 'suspended',
            locals: [{ id: 'err', name: 'ctx.Err()', value: 'deadline exceeded', bytes: 16 }],
          },
          {
            id: 'service',
            functionName: 'main.service',
            status: 'suspended',
            locals: [{ id: 'err', name: 'ctx.Err()', value: 'deadline exceeded', bytes: 16 }],
          },
          {
            id: 'repository',
            functionName: 'main.repository',
            status: 'suspended',
            locals: [
              { id: 'err', name: 'ctx.Err()', value: 'nil', bytes: 16 },
              { id: 'from', name: 'ctx', value: 'Background()', bytes: 16 },
            ],
          },
          {
            id: 'db',
            functionName: 'main.db',
            status: 'active',
            locals: [{ id: 'err', name: 'ctx.Err()', value: 'nil', bytes: 16 }],
          },
        ],
        focusFrameId: 'repository',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · severed · every ctx.Err() value is a STAGE line, and SEVERED names repository; a context.Context interface value is 16 bytes on amd64`,
      });

const choiceFigure = (width: number, height: number): ComparisonSpec => ({
        id: 'go-context-propagation.choice',
        family: 'comparison',
        view: 'table',
        viewport: { width, height },
        subject: 'the ctx sent down',
        question: 'Forward the caller’s context, or start a fresh root?',
        options: [
          { id: 'propagate', label: 'forward', detail: 'repository(ctx)' },
          { id: 'sever', label: 'new root', detail: 'Background()' },
        ],
        criteria: [
          { id: 'chunks', label: 'Chunks that ran', unit: 'of 8', first: 1, second: 8, better: 'lower' },
          { id: 'work', label: 'Time spent in db()', unit: 'ms', first: 50, second: 400, better: 'lower' },
          { id: 'returned', label: 'Handler returned after', unit: 'ms', first: 50, second: 403, better: 'lower', decisive: true },
        ],
        verdictOptionId: 'propagate',
        verdictReason: 'A 25 ms budget only means something if the handler can actually return anywhere near it.',
        title: 'Same four layers, same 25 ms budget, one argument apart',
        subtitle: 'Every cell is a printed counter from the two runs; the severed column is the run quoted throughout.',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · propagated (5 identical runs) and severed (cite:"run", 401–403 ms across 5 runs); db time is the printed chunks completed times the printed 50 ms chunk`,
      });

const fixFigure = (width: number, height: number): StackFramesSpec => ({
        id: 'go-context-propagation.fix',
        family: 'stack-frames',
        view: 'isometric',
        viewport: { width, height },
        growth: 'down',
        frames: [
          {
            id: 'handler',
            functionName: 'main.handler',
            status: 'suspended',
            locals: [{ id: 'err', name: 'ctx.Err()', value: 'deadline exceeded', bytes: 16 }],
          },
          {
            id: 'service',
            functionName: 'main.service',
            status: 'suspended',
            locals: [{ id: 'err', name: 'ctx.Err()', value: 'deadline exceeded', bytes: 16 }],
          },
          {
            id: 'repository',
            functionName: 'main.repository',
            status: 'active',
            locals: [{ id: 'err', name: 'ctx.Err()', value: 'deadline exceeded', bytes: 16 }],
          },
          {
            id: 'db',
            functionName: 'main.db',
            status: 'returned',
            locals: [{ id: 'err', name: 'ctx.Err()', value: 'deadline exceeded', bytes: 16 }],
          },
        ],
        focusFrameId: 'repository',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · propagated · all four STAGE lines report deadline exceeded and no SEVERED line is printed; completed=1 from the EVENT line`,
      });

export const goContextPropagation: GoLesson = {
  id: 'go-context-propagation',
  module: '07 · Concurrency',
  title: 'Cancellation has to travel',
  premise: 'A deadline is a value. It reaches the work that can honour it only if every call passes it on.',
  toolchain: GO,
  scenes: [
    {
      id: 'symptom',
      beat: 'symptom',
      stageLabel: 'SYMPTOM · CAPTURED RUN',
      heading: 'The caller gave up at 25 ms. The work ran 403.',
      subtitle: 'All eight 50 ms chunks completed, and 350 ms of them ran with nobody left waiting.',
      figureReason:
        'The program was written to report its own facts, so the strongest possible figure is the report: a terminal artifact holding the run this lesson quotes, with the numbers it printed.',
      code: {
        caption: 'main.go · service()',
        text: ['func service(ctx context.Context) {', '\tdefer stage("service", ctx)', '\trepository(context.Background())', '}'].join('\n'),
      },
      evidence: {
        caption: 'go run .',
        text: [
          'budget: 25ms',
          'chunks planned: 8',
          'chunks completed: 8 of 8',
          'work after cancel: 350ms',
          'handler returned: 403ms',
          'cancellation reached: service',
        ].join('\n'),
      },
      determinism:
        'NOT reproducible, and not expected to be: 5 runs produced 3 distinct stdout and 1 distinct stderr. Declared cite:"run" — the lesson quotes THIS captured run verbatim; the other runs are recorded above so the spread can be checked. No value here is averaged or rounded.',
      truth: { file: TRUTH_FILE, capture: 'severed' },
      quoted: [
        'budget: 25ms',
        'chunk: 50ms',
        'chunks planned: 8',
        'work planned: 400ms',
        'chunks completed: 8 of 8',
        'work after cancel: 350ms',
        'handler returned: 403ms',
        'cancellation reached: service',
        'verdict: 8 of 8 chunks ran · 403ms',
        'cost: 350ms wasted',
      ],
      spec: (width) => ({
        id: 'go-context-propagation.symptom',
        family: 'execution-artifact',
        view: 'artifact',
        artifactKind: 'terminal',
        surface: 'terminal',
        viewport: { width, height: artifactTerminalHeight(width, 9) },
        context: { tabLabel: 'go run', exitCode: 0 },
        title: 'A 25 ms budget, a 403 ms request',
        lines: [
          { id: 'command', kind: 'command', text: 'go run .' },
          { id: 'budget', kind: 'output', text: 'budget: 25ms' },
          { id: 'chunk', kind: 'output', text: 'chunk: 50ms' },
          { id: 'planned', kind: 'output', text: 'chunks planned: 8' },
          { id: 'completed', kind: 'output', text: 'chunks completed: 8 of 8' },
          { id: 'wasted', kind: 'evidence', text: 'work after cancel: 350ms' },
          { id: 'returned', kind: 'evidence', text: 'handler returned: 403ms' },
          { id: 'reached', kind: 'evidence', text: 'cancellation reached: service' },
          { id: 'verdict', kind: 'muted', text: 'verdict: 8 of 8 chunks ran · 403ms' },
        ],
        focusLineId: executionArtifactLineId('reached'),
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · severed · cite:"run" — this run verbatim; across 5 runs only the handler total moved, 401–403 ms`,
      }),
    },
    {
      id: 'mechanism',
      beat: 'mechanism',
      stageLabel: 'MECHANISM · WHERE IT STOPS',
      heading: 'The cancel stopped one frame short of the work.',
      subtitle: 'Each layer reports the context it held when it returned. Two hold a deadline, two nil.',
      figureReason:
        'The severance is a property of the call stack: which frame still holds the caller’s context and which one is holding a fresh root. Stack frames is the view whose only claim is real call depth, and a context is a per-frame value.',
      code: {
        caption: 'main.go · stage()',
        text: 'func stage(name string, ctx context.Context) { seen[name] = ctx.Err() }',
      },
      evidence: {
        caption: 'go run . · STAGE lines',
        text: [
          'STAGE handler | handler | cancelled | deadline exceeded',
          'STAGE service | service | cancelled | deadline exceeded',
          'STAGE repository | repository | working | ctx.Err()=nil',
          'STAGE db | db | working | ctx.Err()=nil',
          'SEVERED repository',
        ].join('\n'),
      },
      determinism:
        'NOT reproducible, and not expected to be: 5 runs produced 3 distinct stdout and 1 distinct stderr. The STAGE and SEVERED lines were identical in every recorded run.',
      truth: { file: TRUTH_FILE, capture: 'severed' },
      quoted: [
        'STAGE handler | handler | cancelled | deadline exceeded',
        'STAGE service | service | cancelled | deadline exceeded',
        'STAGE repository | repository | working | ctx.Err()=nil',
        'STAGE db | db | working | ctx.Err()=nil',
        'SEVERED repository',
      ],
      spec: (width) => mechanismFigure(width, fittedHeight((height) => mechanismFigure(width, height), 546)),
    },
    {
      id: 'trace',
      beat: 'trace',
      stageLabel: 'TRACE · TO SCALE',
      heading: '350 ms of work happened after the deadline.',
      subtitle: 'The budget expired at 25 ms. Chunk 1 finished at 50, and chunk 8 at 400.',
      figureReason:
        'Order alone would hide the shape of this: the deadline is a hair from the origin and the work runs on for the rest of the rail. A timeline places every moment by elapsed time, so the gap is drawn at its real size.',
      code: {
        caption: 'main.go · the budget and the chunk',
        text: ['const (', '\tbudget  = 25 * time.Millisecond', '\tchunk   = 50 * time.Millisecond', '\tplanned = 8', '\tchunkMs = 50', ')'].join('\n'),
      },
      evidence: {
        caption: 'go run . · EVENT lines',
        text: [
          'EVENT H | write | WithTimeout(25ms) | 0 | the handler gives itself a budget',
          'EVENT R | write | db(ctx) | 1 | chunk 1 finished, 50ms in',
          'EVENT R | write | db(ctx) | 8 | chunk 8 finished, 400ms in',
          'work after cancel: 350ms',
          'handler returned: 403ms',
        ].join('\n'),
      },
      determinism:
        'NOT reproducible, and not expected to be: 5 runs produced 3 distinct stdout. Only the handler total moved, 401–403 ms; every chunk boundary was identical.',
      truth: { file: TRUTH_FILE, capture: 'severed' },
      quoted: [
        'EVENT H | write | WithTimeout(25ms) | 0 | the handler gives itself a budget',
        'EVENT R | write | db(ctx) | 1 | chunk 1 finished, 50ms in',
        'EVENT R | write | db(ctx) | 2 | chunk 2 finished, 100ms in',
        'EVENT R | write | db(ctx) | 8 | chunk 8 finished, 400ms in',
        'budget: 25ms',
        'work after cancel: 350ms',
        'handler returned: 403ms',
      ],
      spec: (width) => ({
        id: 'go-context-propagation.trace',
        family: 'primitive-timeline',
        view: 'sequence',
        viewport: { width, height: 490 },
        subject: 'one severed request',
        unit: 'ms',
        events: [
          { id: 'expires', label: 'budget expires', at: 25, clock: '25ms', detail: 'WithTimeout(25ms) · the caller is gone' },
          { id: 'chunk-1', label: 'chunk 1 finished', at: 50, clock: '50ms' },
          { id: 'chunk-8', label: 'chunk 8 finished', at: 400, clock: '400ms' },
          { id: 'returned', label: 'handler returns', at: 403, clock: '403ms', detail: '403 ms spent on a 25 ms budget' },
        ],
        spans: [{ id: 'wasted', label: 'work after cancel', from: 'chunk-1', to: 'chunk-8' }],
        focusEventId: 'expires',
        title: 'The deadline fired at 25 ms; the work ran to 403',
        subtitle: 'Chunk boundaries and the handler total are printed values, so every gap on this rail is a measured one.',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · severed · chunk times from the EVENT lines, 350 ms from "work after cancel", 403 ms from "handler returned"`,
      }),
    },
    {
      id: 'choice',
      beat: 'choice',
      stageLabel: 'CHOICE · ONE ARGUMENT',
      heading: 'One argument decides whether the deadline travels.',
      subtitle: 'repository(ctx) or repository(context.Background()); the four layers are identical.',
      figureReason:
        'Two real edits to one line, each measured on the same three counters. The comparison derives each row’s winner from a declared direction and lets the wasted-work row carry the decision.',
      code: {
        caption: 'main.go · the one-line difference',
        text: ['func service(ctx context.Context) {', '\tdefer stage("service", ctx)', '\trepository(ctx)', '}'].join('\n'),
      },
      evidence: {
        caption: 'go run . · propagated',
        text: ['chunks completed: 1 of 8', 'work after cancel: 0ms', 'handler returned: 50ms'].join('\n'),
      },
      determinism: '5 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'propagated' },
      quoted: ['chunks completed: 1 of 8', 'work after cancel: 0ms', 'handler returned: 50ms'],
      spec: (width) => choiceFigure(width, fittedHeight((height) => choiceFigure(width, height), 510)),
    },
    {
      id: 'fix',
      beat: 'fix',
      stageLabel: 'FIX · ONE DEADLINE',
      heading: 'Now every frame holds the same deadline.',
      subtitle: 'The repository checks ctx.Done() between chunks, so it returns after the first one.',
      figureReason:
        'The same claim as the mechanism scene, about the same four frames, so the same figure: only the values in the locals band change, which is the whole point.',
      code: {
        caption: 'main.go · repository()',
        text: ['\t\tselect {', '\t\tcase <-ctx.Done():', '\t\t\treturn', '\t\tdefault:', '\t\t}'].join('\n'),
      },
      evidence: {
        caption: 'go run . · STAGE lines',
        text: [
          'STAGE handler | handler | cancelled | deadline exceeded',
          'STAGE service | service | cancelled | deadline exceeded',
          'STAGE repository | repository | cancelled | deadline exceeded',
          'STAGE db | db | cancelled | deadline exceeded',
        ].join('\n'),
      },
      determinism: '5 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'propagated' },
      quoted: [
        'STAGE handler | handler | cancelled | deadline exceeded',
        'STAGE service | service | cancelled | deadline exceeded',
        'STAGE repository | repository | cancelled | deadline exceeded',
        'STAGE db | db | cancelled | deadline exceeded',
        'EVENT R | read | <-ctx.Done() | 1 | the repository sees the cancel and stops',
        'chunks completed: 1 of 8',
      ],
      spec: (width) => fixFigure(width, fittedHeight((height) => fixFigure(width, height), 546)),
    },
    {
      id: 'evidence',
      beat: 'evidence',
      stageLabel: 'EVIDENCE · THE SAME RUN',
      heading: 'One chunk ran. The handler returned in 50 ms.',
      subtitle: 'Identical program, identical budget; the only change is which context goes down the chain.',
      figureReason:
        'The payoff has to be the same instrument as the symptom, or the comparison is rhetorical rather than measured. Same terminal, same counters, the other run.',
      code: {
        caption: 'main.go · the counters',
        text: [
          '\tfmt.Printf("chunks completed: %d of %d\\n", completed, planned)',
          '\tfmt.Printf("work after cancel: %dms\\n", afterCut*chunkMs)',
          '\tfmt.Printf("handler returned: %dms\\n", handlerMs)',
          '\tfmt.Printf("cancellation reached: %s\\n", reached)',
        ].join('\n'),
      },
      evidence: {
        caption: 'go run .',
        text: [
          'chunks completed: 1 of 8',
          'work after cancel: 0ms',
          'handler returned: 50ms',
          'cancellation reached: db',
          'verdict: 1 of 8 chunks ran · 50ms',
          'cost: 0ms wasted',
        ].join('\n'),
      },
      determinism: '5 consecutive run(s), identical output every time.',
      truth: { file: TRUTH_FILE, capture: 'propagated' },
      quoted: [
        'chunks completed: 1 of 8',
        'work after cancel: 0ms',
        'handler returned: 50ms',
        'cancellation reached: db',
        'verdict: 1 of 8 chunks ran · 50ms',
        'cost: 0ms wasted',
      ],
      spec: (width) => ({
        id: 'go-context-propagation.evidence',
        family: 'execution-artifact',
        view: 'artifact',
        artifactKind: 'terminal',
        surface: 'terminal',
        viewport: { width, height: artifactTerminalHeight(width, 8) },
        context: { tabLabel: 'go run', exitCode: 0 },
        title: 'The chain that stops',
        lines: [
          { id: 'command', kind: 'command', text: 'go run .' },
          { id: 'budget', kind: 'output', text: 'budget: 25ms' },
          { id: 'planned', kind: 'output', text: 'chunks planned: 8' },
          { id: 'completed', kind: 'evidence', text: 'chunks completed: 1 of 8' },
          { id: 'wasted', kind: 'output', text: 'work after cancel: 0ms' },
          { id: 'returned', kind: 'evidence', text: 'handler returned: 50ms' },
          { id: 'reached', kind: 'evidence', text: 'cancellation reached: db' },
          { id: 'verdict', kind: 'muted', text: 'verdict: 1 of 8 chunks ran · 50ms' },
        ],
        focusLineId: executionArtifactLineId('reached'),
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · propagated · stdout retained verbatim, 5 identical runs`,
      }),
    },
    {
      id: 'model',
      beat: 'model',
      stageLabel: 'MODEL · WHY IT FOLLOWS',
      heading: 'A new root cannot inherit anything.',
      subtitle: 'Cancellation moves down a tree of contexts, so it reaches only what you actually passed.',
      figureReason:
        'The lesson ends on an implication about a data structure, and an annotated argument is the only figure that makes each step depend, visibly, on the one above it.',
      code: {
        caption: 'main.go · the severed link',
        text: ['func service(ctx context.Context) {', '\tdefer stage("service", ctx)', '\trepository(context.Background())', '}'].join('\n'),
      },
      evidence: {
        caption: 'go run . · severed',
        text: ['cancellation reached: service', 'SEVERED repository'].join('\n'),
      },
      determinism:
        'NOT reproducible, and not expected to be: 5 runs produced 3 distinct stdout. The SEVERED line and the layer it names were identical in every recorded run.',
      truth: { file: TRUTH_FILE, capture: 'severed' },
      quoted: ['cancellation reached: service', 'SEVERED repository', 'chunks completed: 8 of 8'],
      spec: (width) => ({
        id: 'go-context-propagation.model',
        family: 'complexity-argument',
        view: 'annotated-argument',
        viewport: { width, height: argumentHeight(width, 4) },
        claim: 'A fresh root inherits no cancellation',
        steps: [
          {
            id: 'value',
            kind: 'given',
            expression: 'ctx holds done and deadline',
            rationale: 'WithTimeout returns a child holding the parent cancel plus its own 25 ms deadline.',
          },
          {
            id: 'downward',
            kind: 'transform',
            expression: 'a child ends with its parent',
            rationale: 'Cancellation moves down the context tree. Never up, never into a tree it is not in.',
          },
          {
            id: 'root',
            kind: 'transform',
            expression: 'Background() has no parent',
            rationale: 'No parent, no deadline, no done channel, so nothing beneath it can learn anything.',
          },
          {
            id: 'conclusion',
            kind: 'conclusion',
            expression: 'the chain stops at the cut',
            rationale: 'Severed: reached service, 8 of 8 chunks ran. Propagated: reached db, 1 of 8 ran.',
          },
        ],
        focusStepId: 'conclusion',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · severed and propagated · both conclusions are printed "cancellation reached" and "chunks completed" lines`,
      }),
    },
  ],
};
